import {Card, Form, Spin} from 'antd';
import React, {Key, useCallback, useEffect, useMemo, useState} from 'react';

import {ActionBar} from 'components/@common/widgets/action-bar';
import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {AIS_ROUTES} from 'components/ais/ais.constants';
import {SimpleActionButton} from 'components/form/components';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {selectContextDataWithLookups} from 'modules/context/context-selector';
import {usePagination} from 'shared/hooks/use-pagination';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {useAppSelector} from 'store/config/hooks';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {IRisksRequestDto} from '../../api/types';
import {useRisksRequestsColumns} from '../../hooks/use-risks-requests-columns';
import {RisksRequestsService} from '../../services';
import {SendResponseForm} from '../send-response-form';

export const Page = () => {
    const [tableData, setTableData] = useState<IRisksRequestDto[]>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const [sendResponseForm] = Form.useForm();

    const {pageSize, handleChange} = usePagination({pageDefault: 1, pageSizeDefault: 10});

    const {hasUserRoleFunction} = useUserRoleFunctions({sections: [AIS_ROUTES.RISKS_REQUESTS]});

    const isCreateAllowed = hasUserRoleFunction(EUserRoleFunction.CREATE);

    const contextDataWithLookups = useAppSelector(selectContextDataWithLookups);
    const {organizationId} = contextDataWithLookups;

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const {data} = await RisksRequestsService.get({
                paginationPageSize: pageSize,
                paginationCurrent: currentPage,
                organizationId,
            });
            setTableData(data.dataTable);
            setTotalRows(data.recordsTotal);
        } catch (e) {
            console.error('Ошибка получения списка запросов: ', e);
        } finally {
            setIsLoading(false);
        }
    }, [pageSize, currentPage, organizationId]);

    useEffect(() => {
        fetchData();
    }, [pageSize, currentPage, organizationId]);

    const columns = useRisksRequestsColumns({isCreateAllowed, onRefreshed: fetchData});

    const {customTableJSX} = useCustomTable({
        rowKey: 'id',
        pagination: {
            position: ['bottomLeft'],
            total: totalRows,
            pageSize,
            onChange: (value: number) => {
                handleChange(value);
                setCurrentPage(value);
            },
        },
        rowSelection: {selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys)},
        className: 'mt-5',
        dataSource: tableData,
        bordered: true,
        columns,
    });

    const selectedRequest = useMemo(
        () => tableData?.find((row) => row.id === selectedRowKeys[0]),
        [tableData, selectedRowKeys]
    );

    const isSendResponseDisabled = selectedRowKeys.length !== 1;

    const sendResponseModal = useMemo(
        () => (
            <ModalOpenerComponent
                component={
                    <SimpleActionButton
                        disabled={isSendResponseDisabled}
                        type="primary"
                        title="Отправить ответ"
                    />
                }
                modalProps={{title: 'Вызвать сервис'}}
                controlLabels={{save: 'Запуск'}}
                handleSave={(setIsModalOpen) => {
                    sendResponseForm.submit();
                    setIsModalOpen(false);
                }}
            >
                <SendResponseForm
                    form={sendResponseForm}
                    request={selectedRequest}
                    fetchData={fetchData}
                />
            </ModalOpenerComponent>
        ),
        [isSendResponseDisabled, selectedRequest]
    );

    return (
        <Card title="Запросы сведений о рисках">
            <ActionBar>
                <section className="d-flex justify-content-between w-100">
                    <div className="d-flex gap-2">{isCreateAllowed && sendResponseModal}</div>
                </section>
            </ActionBar>

            <Spin spinning={isLoading}>{customTableJSX}</Spin>
        </Card>
    );
};
